import { saveFile, shareFile, canShareFiles } from '../platform';
import { getCompanyDetails, type CompanyFields } from './company';

/**
 * Excel and PDF export.
 *
 * Both libraries are loaded with a dynamic import, so roughly 800 KB of code
 * only downloads when somebody actually exports something. Reports are the
 * least-used part of a daily workflow — making every login pay for them would
 * be the wrong trade, especially on a site connection.
 *
 * Everything is generated in the browser. There is no server rendering a PDF,
 * which is why exports cost nothing to run.
 */

export interface ExportColumn {
  key: string;
  label: string;
  /** Right-aligned in the PDF and in Excel. */
  numeric?: boolean;
  /**
   * A rupee amount. Excel receives the raw number so the column can be summed,
   * and the PDF drops the ₹ symbol (see pdfText below).
   */
  money?: boolean;
}

/**
 * Rows carry both a display string and, for numeric columns, the raw value
 * under `_key` — `amount` reads "₹39,200" while `_amount` is 3920000 paise.
 * Excel wants the number; screen and PDF want the string.
 */
function rawNumber(row: Row, col: ExportColumn): number | null {
  const raw = row[`_${col.key}`];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return col.money ? raw / 100 : raw;
}

/**
 * jsPDF's built-in Helvetica is Latin-1 only and has no glyph for ₹ (U+20B9).
 * Left in, it renders as garbage and corrupts the rest of the cell. Embedding a
 * Unicode font would add several hundred KB to every export for one character,
 * so the symbol is dropped and the header states the currency once instead.
 */
function pdfText(value: unknown): string {
  return String(value ?? '')
    .replace(/₹\s?/g, '')
    .replace(/[—–]/g, '-')
    .replace(/[^\x20-\xFF\n]/g, '');   // anything else outside Latin-1
}

export interface ExportMeta {
  title: string;
  subtitle?: string;
  /** Shown under the title — the filters that produced these rows. */
  filters?: string[];
}

type Row = Record<string, unknown>;

/** Safe for Windows, macOS and Android filenames. */
function fileStem(title: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}-${stamp}`;
}

/**
 * Draws a professional black and white letterhead header on PDF (no background colors).
 */
function drawCompanyHeader(
  doc: {
    setFont: (font: string, style?: string) => void;
    setFontSize: (size: number) => void;
    setTextColor: (r: number, g?: number, b?: number) => void;
    setDrawColor: (r: number, g?: number, b?: number) => void;
    setLineWidth: (width: number) => void;
    line: (x1: number, y1: number, x2: number, y2: number) => void;
    text: (text: string, x: number, y: number, options?: { align?: 'left' | 'center' | 'right' }) => void;
  },
  company: CompanyFields,
  width: number,
): number {
  const companyName = company.name || 'VNS Contruction';
  const locationText = [company.address, company.city].filter(Boolean).join(', ') || '90/14, VIP Thamarai Nagar, Kollampalayam, Erode-638002';
  const phoneVal = company.phone || '+91 9659611044';
  const emailVal = company.email || 'vnsconstruction@hotmail.com';
  const contactText = [phoneVal, emailVal].filter(Boolean).join('   |   ');

  // Company Name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text(pdfText(companyName), 40, 42);

  // Location (Values only)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(pdfText(locationText), 40, 56);

  // Contacts (Values only)
  doc.text(pdfText(contactText), 40, 68);

  // Professional monochrome divider line
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);
  doc.line(40, 78, width - 40, 78);

  return 88;
}

/* -------------------------------- Excel -------------------------------- */

export async function exportExcel(
  columns: ExportColumn[],
  rows: Row[],
  meta: ExportMeta,
): Promise<void> {
  const XLSX = await import('xlsx');

  // Title and filters as text rows above the table, so a printed sheet still
  // says what it is and what it was filtered to.
  const header: unknown[][] = [[meta.title]];
  if (meta.subtitle) header.push([meta.subtitle]);
  if (meta.filters?.length) header.push([meta.filters.join('   ·   ')]);
  header.push([]);
  header.push(columns.map((c) => c.label));

  // Numeric columns go in as real numbers so an accountant can sum, sort and
  // pivot them. Exporting "₹39,200" as text makes the spreadsheet useless for
  // the one thing a spreadsheet is for.
  const body = rows.map((r) =>
    columns.map((c) => {
      if (c.numeric) {
        const n = rawNumber(r, c);
        if (n !== null) return n;
      }
      return r[c.key] ?? '';
    }),
  );

  const sheet = XLSX.utils.aoa_to_sheet([...header, ...body]);

  // Indian digit grouping inside Excel itself, so the cell stays a number you
  // can sum while still reading as ₹45,00,000 rather than 4500000.
  const INR = '[>=10000000]₹##\\,##\\,##\\,##0;[>=100000]₹##\\,##\\,##0;₹##,##0';
  const firstDataRow = header.length;   // 0-indexed; header rows come first
  columns.forEach((c, col) => {
    if (!c.numeric) return;
    for (let i = 0; i < body.length; i++) {
      const ref = XLSX.utils.encode_cell({ r: firstDataRow + i, c: col });
      const cell = sheet[ref] as { t?: string; z?: string } | undefined;
      if (cell && cell.t === 'n') cell.z = c.money ? INR : '#,##0.###';
    }
  });

  // Column widths from the longest cell, capped so one long remark cannot
  // push everything else off the page.
  sheet['!cols'] = columns.map((c, i) => {
    const longest = body.reduce((max, row) => Math.max(max, String(row[i] ?? '').length), c.label.length);
    return { wch: Math.min(Math.max(longest + 2, 10), 42) };
  });

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, meta.title.slice(0, 31)); // Excel caps sheet names at 31
  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;

  await saveFile(
    `${fileStem(meta.title)}.xlsx`,
    buffer,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/* --------------------------------- PDF --------------------------------- */

export async function exportPdf(
  columns: ExportColumn[],
  rows: Row[],
  meta: ExportMeta,
): Promise<void> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  // Landscape: these reports are wide, and a squeezed portrait table is
  // unreadable once printed.
  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();

  let y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(meta.title), 40, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generated ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    width - 40, y + 14, { align: 'right' },
  );

  y += 28;
  if (meta.subtitle) { doc.text(pdfText(meta.subtitle), 40, y); y += 12; }
  if (meta.filters?.length) { doc.text(pdfText(meta.filters.join('  ·  ')), 40, y); y += 12; }

  // The ₹ symbol cannot be drawn in this font, so say it once here rather than
  // leaving a column of bare numbers with no stated currency.
  if (columns.some((c) => c.money)) {
    doc.text('All amounts in Indian Rupees (INR)', 40, y);
    y += 12;
  }

  autoTable(doc, {
    startY: y + 4,
    head: [columns.map((c) => pdfText(c.label))],
    body: rows.map((r) => columns.map((c) => pdfText(r[c.key]))),
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
    headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
    alternateRowStyles: { fillColor: false },
    columnStyles: Object.fromEntries(
      columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
    ),
    margin: { left: 40, right: 40, bottom: 35 },
    didDrawPage: () => {
      const page = doc.getNumberOfPages();
      const height = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(40, height - 26, width - 40, height - 26);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('VNS Contruction', 40, height - 16);
      doc.text(`Page ${page}`, width - 40, height - 16, { align: 'right' });
    },
  });

  await saveFile(`${fileStem(meta.title)}.pdf`, doc.output('blob'), 'application/pdf');
}

/* ------------------------------ multi-section ------------------------------ */

/**
 * One named table within a single-project drill-down — "Client Payment
 * History", "Material Summary", and so on. Unlike a normal report these run
 * together as one document, since they are all about the same project.
 */
export interface ExportSection {
  title: string;
  columns: ExportColumn[];
  rows: Row[];
}

/** A project report with nothing recorded on it yet still exports — the
 *  identity card alone is the point of exporting it. */
export async function exportExcelSections(sections: ExportSection[], meta: ExportMeta): Promise<void> {
  const XLSX = await import('xlsx');

  const aoa: unknown[][] = [[meta.title]];
  if (meta.subtitle) aoa.push([meta.subtitle]);
  if (meta.filters?.length) aoa.push([meta.filters.join('   ·   ')]);

  // Every section's numeric columns still need their own INR cell format, so
  // their sheet positions are tracked as they are written rather than assumed.
  const moneyCells: Array<{ row: number; col: number }> = [];
  const numberCells: Array<{ row: number; col: number }> = [];

  for (const section of sections) {
    aoa.push([]);
    aoa.push([`${section.title} (${section.rows.length} record${section.rows.length === 1 ? '' : 's'})`]);
    if (!section.rows.length) continue;
    aoa.push(section.columns.map((c) => c.label));
    const headerRow = aoa.length - 1;
    section.rows.forEach((r, i) => {
      const rowIdx = headerRow + 1 + i;
      aoa.push(section.columns.map((c, col) => {
        if (c.numeric) {
          const n = rawNumber(r, c);
          if (n !== null) {
            (c.money ? moneyCells : numberCells).push({ row: rowIdx, col });
            return n;
          }
        }
        return r[c.key] ?? '';
      }));
    });
  }

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const INR = '[>=10000000]₹##\\,##\\,##\\,##0;[>=100000]₹##\\,##\\,##0;₹##,##0';
  for (const { row, col } of moneyCells) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })] as { t?: string; z?: string } | undefined;
    if (cell?.t === 'n') cell.z = INR;
  }
  for (const { row, col } of numberCells) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })] as { t?: string; z?: string } | undefined;
    if (cell?.t === 'n') cell.z = '#,##0.###';
  }
  sheet['!cols'] = [{ wch: 26 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 16 }];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, meta.title.slice(0, 31));
  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  await saveFile(
    `${fileStem(meta.title)}.xlsx`,
    buffer,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

export async function exportPdfSections(sections: ExportSection[], meta: ExportMeta): Promise<void> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const footer = () => {
    const page = doc.getNumberOfPages();
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(40, height - 26, width - 40, height - 26);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('VNS Contruction', 40, height - 16);
    doc.text(`Page ${page}`, width - 40, height - 16, { align: 'right' });
  };

  let y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(meta.title), 40, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generated ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    width - 40, y + 14, { align: 'right' },
  );

  y += 28;
  if (meta.subtitle) { doc.text(pdfText(meta.subtitle), 40, y); y += 12; }
  if (meta.filters?.length) { doc.text(pdfText(meta.filters.join('  ·  ')), 40, y); y += 12; }
  doc.text('All amounts in Indian Rupees (INR)', 40, y);
  y += 18;

  for (const section of sections) {
    // An empty section adds nothing to a document meant to be read.
    if (!section.rows.length) continue;
    if (y > height - 130) { doc.addPage(); y = 40; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);
    doc.text(pdfText(`${section.title} (${section.rows.length})`), 40, y);
    y += 8;

    autoTable(doc, {
      startY: y,
      head: [section.columns.map((c) => pdfText(c.label))],
      body: section.rows.map((r) => section.columns.map((c) => pdfText(r[c.key]))),
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
      headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: false },
      columnStyles: Object.fromEntries(
        section.columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
      ),
      margin: { left: 40, right: 40, bottom: 35 },
      didDrawPage: footer,
    });

    // jspdf-autotable attaches this at runtime; the installed types declare
    // the jsPDF document parameter as `any`, so it is not on JsPDF's own type.
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 22;
  }

  await saveFile(`${fileStem(meta.title)}.pdf`, doc.output('blob'), 'application/pdf');
}

/* -------------------------------- Share -------------------------------- */

export function canShare(): boolean {
  return canShareFiles();
}

/** Hands the PDF to WhatsApp, email or whatever the device offers. */
export async function sharePdf(
  columns: ExportColumn[],
  rows: Row[],
  meta: ExportMeta,
): Promise<boolean> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();

  const y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(meta.title), 40, y + 14);

  autoTable(doc, {
    startY: y + 24,
    head: [columns.map((c) => pdfText(c.label))],
    body: rows.map((r) => columns.map((c) => pdfText(r[c.key]))),
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
    headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
    alternateRowStyles: { fillColor: false },
    columnStyles: Object.fromEntries(
      columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
    ),
    margin: { left: 40, right: 40 },
  });

  return shareFile(`${fileStem(meta.title)}.pdf`, doc.output('blob'), meta.title);
}

/** Share the entire multi-section project report as a PDF (WhatsApp, email, etc.). */
export async function sharePdfSections(sections: ExportSection[], meta: ExportMeta): Promise<boolean> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const footer = () => {
    const page = doc.getNumberOfPages();
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(40, height - 26, width - 40, height - 26);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('VNS Contruction', 40, height - 16);
    doc.text(`Page ${page}`, width - 40, height - 16, { align: 'right' });
  };

  let y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(meta.title), 40, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generated ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    width - 40, y + 14, { align: 'right' },
  );

  y += 28;
  if (meta.subtitle) { doc.text(pdfText(meta.subtitle), 40, y); y += 12; }
  if (meta.filters?.length) { doc.text(pdfText(meta.filters.join('  ·  ')), 40, y); y += 12; }
  doc.text('All amounts in Indian Rupees (INR)', 40, y);
  y += 18;

  for (const section of sections) {
    if (!section.rows.length) continue;
    if (y > height - 130) { doc.addPage(); y = 40; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);
    doc.text(pdfText(`${section.title} (${section.rows.length})`), 40, y);
    y += 8;
    autoTable(doc, {
      startY: y,
      head: [section.columns.map((c) => pdfText(c.label))],
      body: section.rows.map((r) => section.columns.map((c) => pdfText(r[c.key]))),
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
      headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
      alternateRowStyles: { fillColor: false },
      columnStyles: Object.fromEntries(
        section.columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
      ),
      margin: { left: 40, right: 40, bottom: 35 },
      didDrawPage: footer,
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 22;
  }

  return shareFile(`${fileStem(meta.title)}.pdf`, doc.output('blob'), meta.title);
}

/* ------------------------- Single-section helpers ------------------------- */

/** Download a single accordion section as an Excel file. */
export async function exportExcelSection(section: ExportSection, meta: ExportMeta): Promise<void> {
  const XLSX = await import('xlsx');
  const aoa: unknown[][] = [
    [meta.title],
    ...(meta.subtitle ? [[meta.subtitle]] : []),
    ...(meta.filters?.length ? [[meta.filters.join('   ·   ')]] : []),
    [],
    [`${section.title} (${section.rows.length} record${section.rows.length === 1 ? '' : 's'})`],
  ];

  const moneyCells: Array<{ row: number; col: number }> = [];
  const numberCells: Array<{ row: number; col: number }> = [];

  if (section.rows.length) {
    aoa.push(section.columns.map((c) => c.label));
    const headerRow = aoa.length - 1;
    section.rows.forEach((r, i) => {
      const rowIdx = headerRow + 1 + i;
      aoa.push(section.columns.map((c, col) => {
        if (c.numeric) {
          const n = rawNumber(r, c);
          if (n !== null) {
            (c.money ? moneyCells : numberCells).push({ row: rowIdx, col });
            return n;
          }
        }
        return r[c.key] ?? '';
      }));
    });
  }

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const INR = '[>=10000000]₹##\\,##\\,##\\,##0;[>=100000]₹##\\,##\\,##0;₹##,##0';
  for (const { row, col } of moneyCells) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })] as { t?: string; z?: string } | undefined;
    if (cell?.t === 'n') cell.z = INR;
  }
  for (const { row, col } of numberCells) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })] as { t?: string; z?: string } | undefined;
    if (cell?.t === 'n') cell.z = '#,##0.###';
  }
  sheet['!cols'] = [{ wch: 26 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 16 }];

  const book = XLSX.utils.book_new();
  const sheetName = section.title.slice(0, 31);
  XLSX.utils.book_append_sheet(book, sheet, sheetName);
  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  await saveFile(
    `${fileStem(`${meta.title} - ${section.title}`)}.xlsx`,
    buffer,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/** Download a single accordion section as a PDF file. */
export async function exportPdfSection(section: ExportSection, meta: ExportMeta): Promise<void> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();

  let y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(meta.title), 40, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generated ${new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`,
    width - 40, y + 14, { align: 'right' },
  );

  y += 28;
  if (meta.subtitle) { doc.text(pdfText(meta.subtitle), 40, y); y += 12; }
  if (meta.filters?.length) { doc.text(pdfText(meta.filters.join('  ·  ')), 40, y); y += 12; }
  if (section.columns.some((c) => c.money)) { doc.text('All amounts in Indian Rupees (INR)', 40, y); y += 12; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(`${section.title} (${section.rows.length})`), 40, y + 8);

  autoTable(doc, {
    startY: y + 20,
    head: [section.columns.map((c) => pdfText(c.label))],
    body: section.rows.map((r) => section.columns.map((c) => pdfText(r[c.key]))),
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
    headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
    alternateRowStyles: { fillColor: false },
    columnStyles: Object.fromEntries(
      section.columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
    ),
    margin: { left: 40, right: 40, bottom: 35 },
    didDrawPage: () => {
      const page = doc.getNumberOfPages();
      const height = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(40, height - 26, width - 40, height - 26);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('VNS Contruction', 40, height - 16);
      doc.text(`Page ${page}`, width - 40, height - 16, { align: 'right' });
    },
  });

  await saveFile(`${fileStem(`${meta.title} - ${section.title}`)}.pdf`, doc.output('blob'), 'application/pdf');
}

/** Share a single accordion section as a PDF via Web Share API. */
export async function sharePdfSection(section: ExportSection, meta: ExportMeta): Promise<boolean> {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const company = await getCompanyDetails();

  const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const width = doc.internal.pageSize.getWidth();

  let y = drawCompanyHeader(doc, company, width);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(pdfText(`${meta.title} — ${section.title}`), 40, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  y += 26;
  if (meta.subtitle) { doc.text(pdfText(meta.subtitle), 40, y); y += 12; }
  if (section.columns.some((c) => c.money)) { doc.text('All amounts in Indian Rupees (INR)', 40, y); y += 8; }

  autoTable(doc, {
    startY: y + 10,
    head: [section.columns.map((c) => pdfText(c.label))],
    body: section.rows.map((r) => section.columns.map((c) => pdfText(r[c.key]))),
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0], fillColor: false },
    headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8.5, fontStyle: 'bold', lineWidth: { top: 1, bottom: 1 }, lineColor: [0, 0, 0] },
    alternateRowStyles: { fillColor: false },
    columnStyles: Object.fromEntries(
      section.columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}]),
    ),
    margin: { left: 40, right: 40, bottom: 35 },
    didDrawPage: () => {
      const page = doc.getNumberOfPages();
      const height = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(40, height - 26, width - 40, height - 26);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('VNS Contruction', 40, height - 16);
      doc.text(`Page ${page}`, width - 40, height - 16, { align: 'right' });
    },
  });

  const filename = `${fileStem(`${meta.title} - ${section.title}`)}.pdf`;
  return shareFile(filename, doc.output('blob'), `${meta.title} — ${section.title}`);
}
